/**
 * Meal Plans List Component
 *
 * Displays a list of meal plans with filtering, search, pagination, and management capabilities.
 * Based on the design from docs/meal-plan-ui/app/meal-plans/page.tsx but adapted
 * for the BiteWise healthcare provider platform design system.
 *
 * @module components/meal-plans/MealPlansList
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTheme } from '@/hooks/useTheme';
import type { MealPlansPagination } from '@/hooks/useMealPlans';
import type { MealPlanFrontend } from '@/types/mealPlan';
import { TitleText } from '@/components/ui/TitleText';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AvatarPhoto } from '@/components/ui/AvatarPhoto';


/**
 * Meal Plan Card Component
 */
const MealPlanCard: React.FC<{
  mealPlan: MealPlanFrontend;
  onDelete: (mealPlanId: string, mealPlanLabel: string) => Promise<void>;
  onView: (mealPlanId: string) => void;
  isDeleting?: boolean;
}> = ({ mealPlan, onDelete, onView, isDeleting = false }) => {
  const { isDark } = useTheme();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Handle delete button click
  const handleDeleteClick = () => {
    setShowConfirmDialog(true);
  };

  // Handle confirmed delete
  const handleConfirmDelete = async () => {
    await onDelete(mealPlan.id, mealPlan.label || 'Untitled Meal Plan');
    setShowConfirmDialog(false);
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
    <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <SubtitleText className="font-semibold">
              {mealPlan.label || 'Untitled Meal Plan'}
            </SubtitleText>
          </div>
          <div className="flex items-center gap-3">
            <AvatarPhoto
              src={mealPlan.creatorPhotoUrl}
              firstName={mealPlan.creatorName.split(' ')[0]}
              lastName={mealPlan.creatorName.split(' ').slice(1).join(' ')}
              size="sm"
              className="flex-shrink-0"
            />
            <BodyText className="text-sm">
              Created by: <span className="font-medium">{mealPlan.creatorName}</span>
            </BodyText>
          </div>
        </div>
      </div>

      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t ${
        isDark ? 'border-secondary-700' : 'border-neutral-200'
      }`}>
        <div className="flex items-center justify-between text-sm min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4 text-neutral-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <BodyText variant="muted" size="sm">
              {Math.ceil(mealPlan.mealCount / mealPlan.creationParameters.mealTypes.length)} days
            </BodyText>
          </div>
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 5M7 13l-1.5-5m0 0h-.9m15.5 5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6h10z" />
            </svg>
            <BodyText variant="muted" size="sm">
              {mealPlan.mealCount} meals
            </BodyText>
          </div>
          <div className="flex items-center">
            <svg className="w-6 h-6 text-green-600 flex-shrink-0 self-center" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            <BodyText variant="muted" size="sm" className="leading-none">
              {mealPlan.totalEstimatedCostAud.toFixed(2)} AUD
            </BodyText>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(mealPlan.id)}
          >
            View
          </Button>
          <Button
            onClick={handleDeleteClick}
            variant="outline"
            size="sm"
            disabled={isDeleting}
            className={`inline-flex items-center ${
              isDark
                ? 'text-error-400 border-error-600 hover:bg-error-900 hover:text-error-300'
                : 'text-error-600 border-error-300 hover:bg-error-50 hover:text-error-700'
            }`}
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </Card>

    {/* Confirm Delete Dialog */}
    <ConfirmDialog
      isOpen={showConfirmDialog}
      onClose={handleCancelDelete}
      onConfirm={handleConfirmDelete}
      title="Delete Meal Plan"
      message={`Are you sure you want to delete "${mealPlan.label || 'Untitled Meal Plan'}"? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      destructive={true}
      isLoading={isDeleting}
    />
    </>
  );
};

/**
 * Props for MealPlansList component
 */
export interface MealPlansListProps {
  /** Array of meal plans to display */
  mealPlans: MealPlanFrontend[];
  /** Pagination information */
  pagination: MealPlansPagination;
  /** Loading state indicator */
  loading: boolean;
  /** Error message if request failed */
  error: string | null;
  /** Function to refresh meal plans data */
  refresh: () => Promise<void>;
  /** Function to handle page changes */
  onPageChange: (page: number) => void;
  /** Function to delete a meal plan */
  deleteMealPlan: (mealPlanId: string, mealPlanLabel: string) => Promise<void>;
}

/**
 * Main Meal Plans List Component
 */
export const MealPlansList: React.FC<MealPlansListProps> = ({
  mealPlans,
  pagination,
  loading,
  error,
  refresh,
  onPageChange,
  deleteMealPlan,
}) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  // Handle delete with loading state
  const handleDelete = async (mealPlanId: string, mealPlanLabel: string) => {
    try {
      setDeletingIds(prev => new Set(prev).add(mealPlanId));
      await deleteMealPlan(mealPlanId, mealPlanLabel);
    } catch (error) {
      console.error('Failed to delete meal plan:', error);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(mealPlanId);
        return newSet;
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <TitleText>Meal Plans</TitleText>
            <BodyText variant="muted">
              Loading meal plans...
            </BodyText>
          </div>
        </div>

        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <Card key={index} className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-neutral-300 rounded w-3/4"></div>
                <div className="h-3 bg-neutral-300 rounded w-1/2"></div>
                <div className="h-3 bg-neutral-300 rounded w-1/4"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <TitleText>Meal Plans</TitleText>
            <BodyText variant="muted">
              Manage and track meal plans for your patients
            </BodyText>
          </div>
        </div>

        <Card className="p-12 text-center">
          <div className="space-y-4">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${
              isDark ? 'bg-error-900/20' : 'bg-error-50'
            }`}>
              <svg className="w-8 h-8 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <SubtitleText className="mb-2">Error Loading Meal Plans</SubtitleText>
              <BodyText variant="muted" className="mb-4">
                {error}
              </BodyText>
              <Button variant="primary" onClick={refresh}>
                Try Again
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <TitleText>Meal Plans</TitleText>
          <BodyText variant="muted">
            Manage and track meal plans for your patients
          </BodyText>
        </div>

        <Button variant="primary" onClick={() => navigate('/meal-plans/create')}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create New Plan
        </Button>
      </div>


      {/* Results Count */}
      <div className="flex items-center justify-between">
        <BodyText variant="muted">
          {pagination.total} meal plan{pagination.total !== 1 ? 's' : ''} found
        </BodyText>

        {pagination.totalPages > 1 && (
          <BodyText variant="muted">
            Page {pagination.page} of {pagination.totalPages}
          </BodyText>
        )}
      </div>

      {/* Meal Plans Grid */}
      <div className="space-y-4">
        {mealPlans.length > 0 ? (
          mealPlans.map((mealPlan) => (
            <MealPlanCard
              key={mealPlan.id}
              mealPlan={mealPlan}
              onDelete={handleDelete}
              onView={(mealPlanId) => navigate(`/meal-plan/${mealPlanId}`)}
              isDeleting={deletingIds.has(mealPlan.id)}
            />
          ))
        ) : (
          <Card className="p-12 text-center">
            <div className="space-y-4">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${
                isDark ? 'bg-secondary-700' : 'bg-neutral-100'
              }`}>
                <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <SubtitleText className="mb-2">No meal plans found</SubtitleText>
                <BodyText variant="muted">
                  Create your first meal plan to get started.
                </BodyText>
              </div>
              <Button variant="primary" className="mt-4" onClick={() => navigate('/meal-plans/create')}>
                Create Your First Meal Plan
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
           currentPage={pagination.page}
           totalPages={pagination.totalPages}
           totalItems={pagination.total}
           itemsPerPage={pagination.perPage}
           onPageChange={onPageChange}
           showInfo={true}
           showPageNumbers={true}
           maxPageNumbers={5}
         />
        </div>
      )}
    </div>
  );
};

export default MealPlansList;